import {
  type AgentContext,
  type IClock,
  type ITelemetryRegistry,
  type ITelemetryTransport,
  LoggerContext,
  TelemetryAgent
} from '@blufin/telemetry';
import type { DependencyList } from 'react';
import { useEffect, useState } from 'react';

/** Production clock: real wall-clock time and a `setTimeout`-backed sleep. Shared by every telemetry host. */
const productionClock: IClock = {
  /** Returns the current wall-clock time. */
  now: () => Date.now(),
  /**
   * Resolves after `ms` milliseconds.
   * @param ms - Delay in milliseconds.
   */
  sleep: (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms))
};

export interface UseTelemetryAgentOptions<TRegistry extends ITelemetryRegistry> {
  /** Injected clock; defaults to a production wall-clock + `setTimeout`. Override in tests. */
  clock?: IClock;
  /** Initial context attached to every emitted batch. */
  context: AgentContext;
  /**
   * Build the transport for this agent. Returning `undefined` disables telemetry (the hook yields no agent
   * and the caller renders without a provider) — eg: convoy-desktop returns `undefined` when
   * `window.electronAPI` is absent (non-Electron contexts).
   */
  createTransport: () => ITelemetryTransport | undefined;
  /** When > 0, drain the queue on this cadence (ms) so low-traffic sessions still ship. Omit to disable. */
  flushIntervalMs?: number;
  /** Invoked once against each freshly-created agent — eg: emit a first-light launch counter + log. */
  onStart?: (agent: TelemetryAgent<TRegistry>) => void;
  /** The typed metric registry for compile-time name/label checks. */
  registry: TRegistry;
}

/**
 * Owns a `TelemetryAgent`'s full lifecycle for a React host: create the transport + agent, register it on
 * `LoggerContext` (so `useLoggerAlpha` routes through it), run the optional `onStart` first-light hook, and
 * on teardown unregister it from `LoggerContext` (via `clearAgent`) then `dispose()` it — the unregister is
 * what lets the next mount (eg: logging back in after the authenticated tree unmounted) register a fresh
 * agent instead of tripping the "already registered" guard. Creation lives in an effect (not `useMemo`) so it is tied to a committed render —
 * `useMemo` may run for renders React never commits (concurrent mode), which would orphan an agent whose
 * constructor already started a self-driving flush loop, with no cleanup. Because `onStart` fires inside the
 * same effect, its events always land on the SAME agent whose flush loop is alive: when `deps` change the
 * agent is rebuilt, the old one's un-flushed events are dropped with it, and the new live agent runs its own
 * `onStart` — net exactly one first-light per stable session.
 * @param options - Agent construction inputs (transport factory, context, registry, flush cadence, onStart).
 * @param options.clock - Injected clock; defaults to a production wall-clock + `setTimeout`.
 * @param options.context - Initial context attached to every emitted batch.
 * @param options.createTransport - Builds the transport; returning `undefined` disables telemetry.
 * @param options.flushIntervalMs - Optional periodic flush cadence (ms); omit to disable.
 * @param options.onStart - Optional hook run once against each freshly-created agent.
 * @param options.registry - The typed metric registry.
 * @param deps - The dependency list that triggers agent re-creation (eg: `[environment, version]`). The
 *   caller must include every value its `createTransport`/`context`/`onStart` closures read.
 * @returns The live agent, or `undefined` when telemetry is disabled or before the first effect commits.
 */
export const useTelemetryAgent = <TRegistry extends ITelemetryRegistry>(
  options: UseTelemetryAgentOptions<TRegistry>,
  deps: DependencyList
): TelemetryAgent<TRegistry> | undefined => {
  const [agent, setAgent] = useState<TelemetryAgent<TRegistry> | undefined>(undefined);
  useEffect(() => {
    const transport = options.createTransport();
    if (!transport) {
      setAgent(undefined);
      return undefined;
    }
    const nextAgent = new TelemetryAgent<TRegistry>({
      clock: options.clock ?? productionClock,
      context: options.context,
      ...(options.flushIntervalMs ? { flushIntervalMs: options.flushIntervalMs } : {}),
      registry: options.registry,
      transport
    });
    LoggerContext.setAgent(nextAgent);
    setAgent(nextAgent);
    options.onStart?.(nextAgent);
    return () => {
      LoggerContext.clearAgent(nextAgent);
      nextAgent.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return agent;
};
